import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CampaignOutlined,
  CheckCircleOutline,
  Close,
  GroupsOutlined,
  MarkChatReadOutlined,
  Search,
  SendOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import useApiEp from "../../hooks/useApiEp";
import PageTitle from "../../widgets/PageTitle";
import TemplatePreview from "../../widgets/TemplatePreview";

const numberFormatter = new Intl.NumberFormat("en-IN");
const formatNumber = (value) => numberFormatter.format(Number(value) || 0);

const formatDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
};

const Metric = ({ icon, label, value, color = "primary.main" }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 2,
      height: "100%",
      borderRadius: 3,
      borderColor: "rgba(15, 118, 110, 0.14)",
      bgcolor: "rgba(255,255,255,0.92)",
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2.5,
          display: "grid",
          placeItems: "center",
          color,
          bgcolor: `${color}12`,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={800} lineHeight={1.1}>
          {formatNumber(value)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const StatusStat = ({ label, value, total, color }) => (
  <Box>
    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700}>
        {formatNumber(value)}
      </Typography>
    </Stack>
    <Box sx={{ height: 5, borderRadius: 4, bgcolor: "grey.100" }}>
      <Box
        sx={{
          width: `${Math.min(100, ((Number(value) || 0) / Math.max(1, total)) * 100)}%`,
          maxWidth: "100%",
          height: "100%",
          borderRadius: 4,
          bgcolor: color,
        }}
      />
    </Box>
  </Box>
);

const TemplateDetailsDialog = ({ item, onClose }) => {
  if (!item) return null;
  const counts = item.statusCounts || {};

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="overline" color="success.main" fontWeight={800}>
          Template performance
        </Typography>
        <Typography variant="h5" fontWeight={800} sx={{ wordBreak: "break-word" }}>
          {item.templateName}
        </Typography>
        <IconButton
          aria-label="Close details"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f7faf9" }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TemplatePreview
              template={item.template}
              height={560}
              margin="0 auto"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2.5}>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Metric
                    icon={<CampaignOutlined />}
                    label="Campaigns launched"
                    value={item.campaignCount}
                    color="#2563eb"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Metric
                    icon={<GroupsOutlined />}
                    label="Audience reached"
                    value={item.audienceReached}
                    color="#059669"
                  />
                </Grid>
              </Grid>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                  Delivery status
                </Typography>
                <Stack spacing={1.5}>
                  <StatusStat
                    label="Sent"
                    value={counts.sent}
                    total={item.totalMessages}
                    color="#2563eb"
                  />
                  <StatusStat
                    label="Delivered"
                    value={counts.delivered}
                    total={item.totalMessages}
                    color="#0d9488"
                  />
                  <StatusStat
                    label="Read"
                    value={counts.read}
                    total={item.totalMessages}
                    color="#16a34a"
                  />
                  <StatusStat
                    label="Queued"
                    value={counts.queued}
                    total={item.totalMessages}
                    color="#d97706"
                  />
                  <StatusStat
                    label="Failed"
                    value={counts.failed}
                    total={item.totalMessages}
                    color="#dc2626"
                  />
                  <StatusStat
                    label="Cancelled"
                    value={counts.cancelled}
                    total={item.totalMessages}
                    color="#64748b"
                  />
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                  Campaigns using this template
                </Typography>
                {item.campaignNames?.length ? (
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    {item.campaignNames.map((name) => (
                      <Chip key={name} label={name} variant="outlined" />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No campaign name was saved for these messages.
                  </Typography>
                )}
              </Paper>

              <Stack direction="row" spacing={3} color="text.secondary">
                <Typography variant="caption">
                  First used: <strong>{formatDate(item.firstUsedOn)}</strong>
                </Typography>
                <Typography variant="caption">
                  Last used: <strong>{formatDate(item.lastUsedOn)}</strong>
                </Typography>
              </Stack>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

const TemplatesUsedAnalytics = () => {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const { data = [], isLoading, isError, error } = useApiEp(
    "/message/get-templates-used"
  );

  const templates = useMemo(
    () => (Array.isArray(data) ? data : data?.templates || []),
    [data]
  );
  const filteredTemplates = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return templates;
    return templates.filter((item) =>
      [item.templateName, item.templateId, ...(item.campaignNames || [])]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(normalizedQuery))
    );
  }, [query, templates]);

  const totals = useMemo(
    () =>
      templates.reduce(
        (summary, item) => ({
          campaigns: summary.campaigns + (Number(item.campaignCount) || 0),
          reached: summary.reached + (Number(item.audienceReached) || 0),
          read: summary.read + (Number(item.statusCounts?.read) || 0),
        }),
        { campaigns: 0, reached: 0, read: 0 }
      ),
    [templates]
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3, pb: 6 }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 4 },
          mb: 3,
          overflow: "hidden",
          position: "relative",
          borderRadius: 4,
          color: "white",
          background:
            "linear-gradient(125deg, #064e3b 0%, #0f766e 55%, #14b8a6 100%)",
          "&::after": {
            content: '""',
            position: "absolute",
            width: 230,
            height: 230,
            borderRadius: "50%",
            right: -70,
            top: -110,
            bgcolor: "rgba(255,255,255,0.10)",
          },
        }}
      >
        <PageTitle sx={{ color: "inherit" }}>Templates Used</PageTitle>
        <Typography sx={{ opacity: 0.84, maxWidth: 680 }}>
          See which WhatsApp templates power your campaigns and how their
          messages progress from send to read.
        </Typography>
      </Paper>

      {isLoading && <LinearProgress sx={{ mb: 3, borderRadius: 5 }} />}
      {isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Could not load template usage: {error?.message || "Unknown error"}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Metric
            icon={<CheckCircleOutline />}
            label="Templates used"
            value={templates.length}
            color="#0f766e"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Metric
            icon={<CampaignOutlined />}
            label="Campaign launches"
            value={totals.campaigns}
            color="#2563eb"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Metric
            icon={<GroupsOutlined />}
            label="Audience reached"
            value={totals.reached}
            color="#059669"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <Metric
            icon={<MarkChatReadOutlined />}
            label="Messages read"
            value={totals.read}
            color="#7c3aed"
          />
        </Grid>
      </Grid>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={800}>
            Template performance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Audience reached excludes queued, failed, and cancelled messages.
          </Typography>
        </Box>
        <TextField
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search template or campaign"
          sx={{ width: { xs: "100%", sm: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {isLoading ? (
        <Grid container spacing={2}>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item}>
              <Skeleton variant="rounded" height={265} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      ) : filteredTemplates.length === 0 && !isError ? (
        <Alert severity="info">
          {query
            ? "No templates match your search."
            : "No WhatsApp template campaign messages were found yet."}
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredTemplates.map((item) => {
            const counts = item.statusCounts || {};
            return (
              <Grid item xs={12} md={6} lg={4} key={`${item.templateId}-${item.templateName}`}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    borderColor: "rgba(15, 118, 110, 0.14)",
                    transition: "transform .2s ease, box-shadow .2s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 14px 35px rgba(15, 23, 42, 0.09)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 2.5, height: "100%" }}>
                    <Stack height="100%" spacing={2}>
                      <Box>
                        <Stack direction="row" justifyContent="space-between" gap={1}>
                          <Typography
                            variant="h6"
                            fontWeight={800}
                            sx={{ wordBreak: "break-word" }}
                          >
                            {item.templateName}
                          </Typography>
                          {item.template?.category && (
                            <Chip
                              size="small"
                              label={item.template.category}
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          ID: {item.templateId}
                        </Typography>
                      </Box>
                      <Divider />
                      <Grid container spacing={1.5}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Campaigns
                          </Typography>
                          <Typography variant="h6" fontWeight={800}>
                            {formatNumber(item.campaignCount)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            Audience reached
                          </Typography>
                          <Typography variant="h6" fontWeight={800} color="success.main">
                            {formatNumber(item.audienceReached)}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Stack direction="row" gap={0.75} flexWrap="wrap">
                        <Chip
                          size="small"
                          icon={<SendOutlined />}
                          label={`${formatNumber(counts.sent)} sent`}
                        />
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          label={`${formatNumber(counts.delivered)} delivered`}
                        />
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={`${formatNumber(counts.read)} read`}
                        />
                      </Stack>
                      <Box sx={{ flexGrow: 1 }} />
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" color="text.secondary">
                          Last used {formatDate(item.lastUsedOn)}
                        </Typography>
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          startIcon={<VisibilityOutlined />}
                          onClick={() => setSelected(item)}
                          sx={{ borderRadius: 2, textTransform: "none" }}
                        >
                          View details
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <TemplateDetailsDialog item={selected} onClose={() => setSelected(null)} />
    </Container>
  );
};

export default TemplatesUsedAnalytics;
